/* The 13x13 matrix: pairs on the diagonal, suited above it, offsuit below. Every cell is
   filled left to right with the mix hero plays the hand with, and bottom to top with how
   often hero holds the hand at all. Hovering a cell says the same in numbers, and a click or
   a tap keeps those numbers up; in edit mode the grid is a brush surface instead. */

import {
  HANDS,
  LABEL,
  ORDER,
  pickByRandom,
  presenceAt,
  type Action,
  type Cell,
  type Charted,
  type EquityRow,
  seatName,
} from "@preflop/core";
import { el, fill } from "./dom";
import type { Tooltip } from "./tooltip";

/** The colour of each action, as the theme names it. */
export const ACTION_COLOR: Readonly<Record<Action, string>> = {
  allin: "var(--allin)",
  raise: "var(--raise)",
  call: "var(--call)",
  fold: "var(--fold)",
};

const pct = (w: number): string => (w * 100).toFixed(1).replace(/\.0$/, "") + " %";

/** One hand's mix as a left-to-right gradient, strongest action first. Null for no mix. */
export function fillFor(dist: Cell): string | null {
  const stops: string[] = [];
  let at = 0;
  for (const a of ORDER) {
    const w = dist[a];
    if (!w) continue;
    const end = at + w * 100;
    stops.push(`${ACTION_COLOR[a]} ${at.toFixed(2)}%`, `${ACTION_COLOR[a]} ${end.toFixed(2)}%`);
    at = end;
  }
  if (!stops.length) return null;
  // A gradient stretches its last colour to the edge, which would paint a 60% raise as a
  // full cell when one action is isolated. The remainder gets the empty-cell colour so the
  // frequency stays readable on its own.
  if (at < 99.995) stops.push(`var(--panel) ${at.toFixed(2)}%`, "var(--panel) 100%");
  return `linear-gradient(to right, ${stops.join(", ")})`;
}

export interface GridView {
  /** The chart to draw. */
  spot: Charted;
  /** Show one action on its own, or null for the whole mix. */
  isolate: Action | null;
  /** The randomizer's 0-99: every hand is painted the one action it names. Null shows the
      mix. */
  roll: number | null;
  /** The tooltip's equity lines: one entry per opponent, as equityRows() built them. */
  equity: readonly EquityRow[];
  /** Whether the grid is a brush surface right now. */
  paint: boolean;
  /** Which chart this is, in a few words: the small label over the hand in the tooltip. The
      grid only knows cells, so the page says it. Left out, the tooltip opens on the hand. */
  label?: string;
  /** Which chart this is, to the grid: a tooltip pinned on one chart does not carry over to
      the next. Left out, every render is taken for the same chart. */
  id?: string;
}

export interface PaintHooks {
  /** Asked on every pointer event, so the page can turn painting on and off freely. */
  enabled(): boolean;
  /** One hand of a stroke. `only` is a shift-stroke: take the painted action off the hand. */
  stroke(hand: string, only: boolean): void;
}

export interface Grid {
  root: HTMLElement;
  render(view: GridView): void;
  bindPaint(hooks: PaintHooks): void;
}

/** Where each hand sits in the grid, for joining up a drag. */
const POS = new Map<string, readonly [number, number]>();
HANDS.forEach((row, r) => row.forEach((h, c) => POS.set(h, [r, c])));

export function createGrid(tip: Tooltip): Grid {
  const root = el("div", { class: "matrix", dataset: { testid: "matrix" } });
  let view: GridView | null = null;
  // The hand the tooltip is up for. The matrix is redrawn under the pointer by every stroke
  // and every late arrival (the equity table), so the box is refilled from the new view
  // rather than left describing the old one.
  let hovered: string | null = null;
  // The hand a click pinned the tooltip to. Pinned, the box stands beside its cell and hover
  // leaves it alone: it was pinned to be read.
  let pinned: string | null = null;

  /** What one roll names for a hand, when a roll is being read at all. */
  const pickFor = (v: GridView, raw: Cell): Action | null =>
    v.roll != null && !v.isolate ? pickByRandom(raw, v.roll) : null;

  function tipRows(v: GridView, hand: string, raw: Cell): Node[] {
    const row = (cls: string, left: (Node | string)[], right: string): HTMLElement =>
      el("div", { class: cls }, el("i", {}, left), el("i", {}, right));
    const rows: Node[] = [
      ...(v.label ? [el("span", { class: "eye" }, v.label)] : []),
      el("b", {}, hand),
    ];
    // Three parts, a line between them: what hero does with the hand, how it fares all-in,
    // and how often hero holds it at all.
    const pick = pickFor(v, raw);
    for (const a of ORDER) {
      const w = raw[a];
      if (!w) continue;
      const dot = el("span", { class: "dot", style: { background: ACTION_COLOR[a] } });
      rows.push(row("r" + (a === pick ? " picked" : ""), [dot, LABEL[a]], pct(w)));
    }
    // How the hand fares all-in against what each opponent showed up with. Read off the
    // per-chart table, so a hover costs one lookup.
    if (v.equity.length) {
      rows.push(el("div", { class: "sep" }));
      for (const o of v.equity) {
        const q = o.map.get(hand);
        rows.push(
          row(
            "r eq",
            [`Equity vs. ${seatName(o.seat)}`],
            q == null ? "—" : (q * 100).toFixed(1) + " %",
          ),
        );
      }
    }
    // A sliver of colour is not a number. Held outright there is nothing to say, so the
    // part only shows up on the hands the height is about.
    const pres = presenceAt(v.spot, hand);
    if (pres < 1)
      rows.push(el("div", { class: "sep" }), row("r reach", ["Reaches here"], pct(pres)));
    return rows;
  }

  function cellFor(v: GridView, h: string): HTMLElement {
    const c = el("div", { class: "cell", dataset: { hand: h } }); // what a stroke paints
    if (h === pinned) c.classList.add("pinned");
    const raw = v.spot.cells[h];
    let dist: Cell | null = raw ?? null;
    if (raw && v.isolate) {
      const w = raw[v.isolate] ?? 0;
      dist = w ? { [v.isolate]: w } : null;
    }
    if (!raw) {
      // outside hero's range
      c.classList.add("dead");
      c.dataset.dead = "1";
    } else if (v.isolate && !dist) c.classList.add("dead");
    // How much of the cell hero actually holds. The cells say what hero does with a hand,
    // not how often hero has it, so this comes off the range.
    const pres = raw ? presenceAt(v.spot, h) : 0;
    const pick = raw ? pickFor(v, raw) : null;
    const bg = pick ? ACTION_COLOR[pick] : dist && fillFor(dist);
    if (bg) {
      const box = el(
        "div",
        {
          class: "pres" + (pres < 1 ? " part" : ""),
          style: { height: `${(pres * 100).toFixed(2)}%` },
        },
        el("div", { class: "fill", style: { background: bg } }),
      );
      // Under a highlighted cell: the mix the solid colour was drawn from, so the answer
      // does not cost the frequencies it came out of.
      if (pick && raw)
        box.append(el("div", { class: "mix", style: { background: fillFor(raw) ?? "" } }));
      c.append(box);
    } else if (raw) c.classList.add("none");
    c.append(el("span", {}, h));
    return c;
  }

  const cellOf = (hand: string): HTMLElement | null =>
    root.querySelector<HTMLElement>(`.cell[data-hand="${hand}"]`);

  function unpin(): void {
    if (!pinned) return;
    cellOf(pinned)?.classList.remove("pinned");
    pinned = null;
    hovered = null;
    tip.hide();
  }

  function render(next: GridView): void {
    // A pinned box is about one hand of one chart, read: another chart, or the brush, ends it.
    if (pinned && (next.paint || next.id !== view?.id || !next.spot.cells[pinned])) unpin();
    view = next;
    root.classList.toggle("paint", next.paint);
    fill(
      root,
      HANDS.map((row) =>
        el(
          "div",
          { class: "mrow" },
          row.map((h) => cellFor(next, h)),
        ),
      ),
    );
    const up = pinned ?? hovered;
    if (!up || !tip.shown) return;
    const raw = next.spot.cells[up];
    // The pinned cell was redrawn with the rest: the box goes beside the new one.
    if (raw) tip.update(pinned ? cellOf(pinned) : null, ...tipRows(next, up, raw));
    else tip.hide();
  }

  const cellAt = (e: Event): HTMLElement | null =>
    e.target instanceof Element ? e.target.closest<HTMLElement>(".cell[data-hand]") : null;

  /* Bound once on the grid and never on a cell, like the brush below: a listener on a cell
     would go with the cell at the next redraw. */
  const leave = (): void => {
    if (pinned) return;
    hovered = null;
    tip.hide();
  };
  root.addEventListener("pointerover", (e) => {
    if (pinned) return;
    const hand = cellAt(e)?.dataset.hand;
    const raw = hand && view ? view.spot.cells[hand] : undefined;
    if (!hand || !raw || !view) return leave();
    if (hand === hovered && tip.shown) return;
    hovered = hand;
    tip.show(e, ...tipRows(view, hand, raw));
  });
  root.addEventListener("pointermove", (e) => {
    if (hovered) tip.move(e);
  });
  root.addEventListener("pointerleave", leave);

  /* A click or a tap pins the tooltip to its hand: a finger has no pointer for it to follow,
     and under a mouse it is a way to keep the numbers up while looking elsewhere. The next
     click anywhere closes it, and opens another if it lands on another hand. On the window,
     since "anywhere" is. Not while painting: there a click is a stroke. */
  addEventListener("click", (e) => {
    if (e.target instanceof Node && tip.root.contains(e.target)) return;
    const was = pinned;
    unpin();
    const cell = cellAt(e);
    const hand = cell?.dataset.hand;
    if (!cell || !hand || hand === was || !root.contains(cell) || !view || view.paint) return;
    const raw = view.spot.cells[hand];
    if (!raw) return;
    pinned = hand;
    cell.classList.add("pinned");
    tip.pin(cell, ...tipRows(view, hand, raw));
  });
  addEventListener("keydown", (e) => {
    if (e.key === "Escape") unpin();
  });

  /* Bound once on the grid and never on a cell: every stroke redraws the matrix under the
     pointer, and a listener on a cell would go with it. */
  function bindPaint(hooks: PaintHooks): void {
    let stroke: Set<string> | null = null; // hands this drag has painted
    let last: string | null = null; // the hand it was over last

    /* A drag reports one position every few cells, faster the quicker it moves. The hands
       between the last one reported and this one were dragged over just the same, so the
       stroke is drawn as a line and not as its samples. */
    const between = (from: string, to: string): string[] => {
      const out: string[] = [];
      const a = POS.get(from);
      const b = POS.get(to);
      if (!a || !b) return out;
      const [r0, c0] = a;
      const [r1, c1] = b;
      const n = Math.max(Math.abs(r1 - r0), Math.abs(c1 - c0));
      for (let i = 1; i < n; i++) {
        const h =
          HANDS[Math.round(r0 + ((r1 - r0) * i) / n)]?.[Math.round(c0 + ((c1 - c0) * i) / n)];
        if (h) out.push(h);
      }
      return out;
    };

    const put = (hand: string, deliberate: boolean, only: boolean): void => {
      if (!stroke || stroke.has(hand)) return;
      // A drag crossing a hand outside hero's range is almost never meant to add it. A click
      // straight on one is, and applyNotes says so afterwards.
      if (!deliberate && !view?.spot.cells[hand]) return;
      stroke.add(hand);
      hooks.stroke(hand, only);
    };

    root.addEventListener("pointerdown", (e) => {
      if (!hooks.enabled() || e.button !== 0) return;
      const hand = cellAt(e)?.dataset.hand;
      if (!hand) return;
      e.preventDefault();
      stroke = new Set();
      put(hand, true, e.shiftKey);
      last = hand;
    });
    root.addEventListener("pointerover", (e) => {
      if (!stroke || !hooks.enabled()) return;
      // A pointerup can go missing — released over another window, swallowed by a dialog.
      // The button itself says whether the stroke is still being drawn.
      if (!(e.buttons & 1)) {
        stroke = null;
        last = null;
        return;
      }
      const hand = cellAt(e)?.dataset.hand;
      if (!hand) return;
      if (last) for (const mid of between(last, hand)) put(mid, false, e.shiftKey);
      put(hand, false, e.shiftKey);
      last = hand;
    });
    // The drag can end anywhere, including off the grid and off the window.
    const end = (): void => {
      stroke = null;
      last = null;
    };
    addEventListener("pointerup", end);
    addEventListener("pointercancel", end);
  }

  return { root, render, bindPaint };
}
